import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CircleDollarSign, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'

import { eventsApi } from '@/services/events'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Empty } from '@/components/ui/empty'
import { cn, formatCurrency } from '@/lib/utils'
import { getErrorMessage } from '@/lib/api'

type EventPaymentPanelProps = {
  jobId: string | number
}

function statusBadgeClass(status: string) {
  switch (status?.toLowerCase()) {
    case 'paid':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200'
    case 'pending':
      return 'bg-amber-50 text-amber-700 border-amber-200'
    case 'overdue':
      return 'bg-red-50 text-red-700 border-red-200'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

export function EventPaymentPanel({ jobId }: EventPaymentPanelProps) {
  const queryClient = useQueryClient()

  const snapshotQuery = useQuery({
    queryKey: ['event-payment-snapshot', jobId],
    queryFn: () => eventsApi.payment.snapshot(jobId),
    staleTime: 30_000,
  })

  const workerStatusQuery = useQuery({
    queryKey: ['event-payment-worker-status', jobId],
    queryFn: () => eventsApi.payment.workerStatus(jobId),
    staleTime: 30_000,
  })

  const generateInvoicesMutation = useMutation({
    mutationFn: () => eventsApi.payment.generateInvoices(jobId),
    onSuccess: (result) => {
      toast.success(`Invoices generated: ${result.created} created, ${result.updated} updated`)
      queryClient.invalidateQueries({ queryKey: ['event-payment-snapshot', jobId] })
      queryClient.invalidateQueries({ queryKey: ['event-payment-worker-status', jobId] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) ?? 'Failed to generate invoices')
    },
  })

  const reconcileMutation = useMutation({
    mutationFn: () => eventsApi.payment.reconcile(jobId),
    onSuccess: () => {
      toast.success('Payment reconciliation complete')
      queryClient.invalidateQueries({ queryKey: ['event-payment-snapshot', jobId] })
      queryClient.invalidateQueries({ queryKey: ['event-payment-worker-status', jobId] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) ?? 'Failed to reconcile payments')
    },
  })

  const snapshot = snapshotQuery.data
  const workers = workerStatusQuery.data ?? []

  return (
    <div className="space-y-4">
      {/* Financial snapshot */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="text-base">Payment Summary</CardTitle>
              <CardDescription>Financial overview for this event</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => generateInvoicesMutation.mutate()}
                disabled={generateInvoicesMutation.isPending}
              >
                {generateInvoicesMutation.isPending ? (
                  <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <CircleDollarSign className="mr-2 h-3.5 w-3.5" />
                )}
                Generate Invoices
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => reconcileMutation.mutate()}
                disabled={reconcileMutation.isPending}
              >
                {reconcileMutation.isPending ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {snapshotQuery.isLoading ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-16 rounded-xl" />
              ))}
            </div>
          ) : snapshot ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-xl border border-border/60 p-3 text-center">
                <p className="text-xs text-muted-foreground">Total</p>
                <p className="mt-1 text-lg font-semibold">{formatCurrency(snapshot.total_amount)}</p>
              </div>
              <div className="rounded-xl border border-border/60 p-3 text-center">
                <p className="text-xs text-muted-foreground">Paid</p>
                <p className="mt-1 text-lg font-semibold text-emerald-600">{formatCurrency(snapshot.paid_amount)}</p>
              </div>
              <div className="rounded-xl border border-border/60 p-3 text-center">
                <p className="text-xs text-muted-foreground">Pending</p>
                <p className="mt-1 text-lg font-semibold text-amber-600">{formatCurrency(snapshot.pending_amount)}</p>
              </div>
              <div className="rounded-xl border border-border/60 p-3 text-center">
                <p className="text-xs text-muted-foreground">Workers</p>
                <p className="mt-1 text-lg font-semibold">{snapshot.workers_count}</p>
              </div>
            </div>
          ) : (
            <Empty className="py-6">
              <p className="text-sm text-muted-foreground">No payment data yet</p>
            </Empty>
          )}
        </CardContent>
      </Card>

      {/* Worker payment status */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Worker Payment Status</CardTitle>
          <CardDescription>Per-worker invoice and payment breakdown</CardDescription>
        </CardHeader>
        <CardContent>
          {workerStatusQuery.isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-14 rounded-xl" />
              ))}
            </div>
          ) : !workers.length ? (
            <Empty className="py-8">
              <CircleDollarSign className="mb-3 h-8 w-8 text-muted-foreground/60" />
              <p className="text-sm font-medium text-foreground">No worker payments</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Generate invoices first to see payment status per worker.
              </p>
            </Empty>
          ) : (
            <div className="space-y-2">
              {workers.map((worker) => (
                <div
                  key={String(worker.worker_id)}
                  className="flex items-center justify-between rounded-xl border border-border/60 p-3"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">{worker.worker_name}</p>
                    {worker.invoice_id ? (
                      <p className="text-xs text-muted-foreground">Invoice #{worker.invoice_id}</p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-medium">{formatCurrency(worker.amount)}</p>
                    <Badge
                      variant="outline"
                      className={cn('text-xs capitalize', statusBadgeClass(worker.status))}
                    >
                      {worker.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
